import { useCallback, useEffect, useState } from 'react';
import {
  listPendingTasks,
  countPendingTasks,
  completeCustomerTask,
  archiveCustomerTask,
  type PendingTaskRow,
} from '../../infra/customer360Repo';
import { showError } from '../../../shared/ui/appToast';
import { reportUserVisibleFailure } from '../../../shared/sentry/sentryUser';

export type { PendingTaskRow };

const PAGE_SIZE = 20;

export function usePendingTasks(visible: boolean) {
  const [tasks, setTasks] = useState<PendingTaskRow[]>([]);
  const [count, setCount] = useState(0);
  const [page, setPage] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [loading, setLoading] = useState(false);

  const loadPage = useCallback(async (newPage: number) => {
    setLoading(true);
    try {
      const [rows, total] = await Promise.all([
        listPendingTasks(PAGE_SIZE, newPage * PAGE_SIZE),
        countPendingTasks(),
      ]);
      setTasks(rows);
      setCount(total);
      setPage(newPage);
      setHasMore((newPage + 1) * PAGE_SIZE < total);
    } catch (err) {
      console.warn('[usePendingTasks] Error loading', err);
      setTasks([]);
      setCount(0);
      setPage(0);
      setHasMore(false);
    } finally {
      setLoading(false);
    }
  }, []);

  const reload = useCallback(() => loadPage(0), [loadPage]);

  const completeTask = useCallback(async (taskId: string) => {
    try {
      await completeCustomerTask(taskId);
      await reload();
    } catch (error: unknown) {
      reportUserVisibleFailure({
        module: 'customers',
        op: 'customer.task_complete',
        error,
        title: 'No se pudo completar la tarea.',
      });
      showError('Tarea', 'No se pudo completar la tarea.');
    }
  }, [reload]);

  const archiveTask = useCallback(async (taskId: string) => {
    try {
      await archiveCustomerTask(taskId);
      await reload();
    } catch (error: unknown) {
      reportUserVisibleFailure({
        module: 'customers',
        op: 'customer.task_archive',
        error,
        title: 'No se pudo archivar la tarea.',
      });
      showError('Tarea', 'No se pudo archivar la tarea.');
    }
  }, [reload]);

  useEffect(() => {
    if (!visible) {
      setTasks([]);
      setCount(0);
      setPage(0);
      setHasMore(false);
      return;
    }

    let cancelled = false;
    setLoading(true);

    void loadPage(0).finally(() => {
      if (!cancelled) setLoading(false);
    });

    return () => {
      cancelled = true;
    };
  }, [visible, loadPage]);

  return {
    tasks,
    count,
    loading,
    page,
    hasMore,
    pageSize: PAGE_SIZE,
    loadPage,
    reload,
    completeTask,
    archiveTask,
  };
}
